import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { useKnowledgeBaseCore } from './useKnowledgeBaseCore';
import { generateEmbedding } from '@/lib/embeddings';

export function useKnowledgeBases() {
  const knowledgeBaseCore = useKnowledgeBaseCore();
  const { user } = useAuth();
  const { toast } = useToast();
  const [sharesLoading, setSharesLoading] = useState(false);
  
  // Add a document to a knowledge base
  const addDocument = async (knowledgeBaseId: string, document: {
    name: string;
    content: string;
    fileType?: string;
    size: number;
  }) => {
    try {
      console.log(`Adding document to KB ${knowledgeBaseId}: ${document.name}`);
      console.log(`Document content length: ${document.content.length} characters`);
      
      // Generate embedding for the document content
      console.log("Generating embedding for document content...");
      const embedding = await generateEmbedding(document.content);
      
      if (embedding) {
        console.log(`Successfully generated embedding with ${embedding.length} dimensions`);
        console.log(`First few values of embedding:`, embedding.slice(0, 5));
      } else {
        console.warn("Failed to generate embedding, document will be saved without embedding");
      }
      
      // Create document data object
      const documentData: {
        knowledge_base_id: string;
        name: string;
        content: string;
        file_type: string;
        size: number;
        embedding?: number[] | null;
      } = {
        knowledge_base_id: knowledgeBaseId,
        name: document.name,
        content: document.content,
        file_type: document.fileType || 'markdown',
        size: document.size,
      };
      
      // Only add embedding if it was successfully generated
      if (embedding) {
        documentData.embedding = embedding;
      }
      
      // Insert the document into the database
      console.log("Inserting document into database with embedding:", !!embedding);
      const { data, error } = await supabase
        .from('knowledge_base_documents')
        .insert(documentData)
        .select()
        .single();

      if (error) {
        console.error('Error details:', error);
        throw error;
      }
      
      toast({
        title: 'Success',
        description: 'Document added successfully with embeddings',
      });
      
      return data.id;
    } catch (error: any) {
      console.error('Error adding document:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to add document',
        variant: 'destructive'
      });
      return null;
    }
  };

  // Update a document
  const updateDocument = async (documentId: string, updates: {
    name?: string;
    content?: string;
    size?: number;
  }) => {
    try {
      const updateData: any = {
        ...updates,
        updated_at: new Date().toISOString()
      };
      
      // Generate new embedding if content was updated
      if (updates.content) {
        const embedding = await generateEmbedding(updates.content);
        if (embedding) {
          updateData.embedding = embedding;
        }
      }
      
      const { error } = await supabase
        .from('knowledge_base_documents')
        .update(updateData)
        .eq('id', documentId);

      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Document updated successfully with new embeddings',
      });
      
      return true;
    } catch (error) {
      console.error('Error updating document:', error);
      toast({
        title: 'Error',
        description: 'Failed to update document',
        variant: 'destructive'
      });
      return false;
    }
  };

  // Delete a document
  const deleteDocument = async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Document deleted successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete document',
        variant: 'destructive'
      });
      return false;
    }
  };
  
  // Get knowledge base shares
  const getKnowledgeBaseShares = async (knowledgeBaseId: string) => {
    try {
      setSharesLoading(true);
      
      // Modified query to avoid using relationship syntax
      const sharesQuery = await supabase
        .from('knowledge_base_shares')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId);
      
      if (sharesQuery.error) throw sharesQuery.error;
      
      const shares = sharesQuery.data || [];
      
      // Get unique user IDs to fetch their information
      const userIds = [...new Set(shares.map(share => share.user_id))];
      
      // Only fetch profiles if there are shares
      if (userIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', userIds);
        
        if (profilesError) throw profilesError;
        
        // Create a mapping of user IDs to user info for easy lookup
        const userInfoMap = (profiles || []).reduce((map, profile) => {
          map[profile.id] = {
            user_name: profile.full_name || 'Unknown User',
          };
          return map;
        }, {} as Record<string, { user_name: string }>);
        
        // Enhance shares with user information
        return shares.map(share => ({
          ...share,
          user_name: userInfoMap[share.user_id]?.user_name || 'Unknown User',
          access_level: share.access_level as 'View' | 'Edit' | 'Manage'
        }));
      }
      
      return shares.map(share => ({
        ...share,
        user_name: 'Unknown User',
        access_level: share.access_level as 'View' | 'Edit' | 'Manage'
      }));
    } catch (error) {
      console.error('Error fetching knowledge base shares:', error);
      return [];
    } finally {
      setSharesLoading(false);
    }
  };
  
  // Share a knowledge base with a user
  const shareKnowledgeBase = async (
    knowledgeBaseId: string,
    userId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ) => {
    if (!user) return false;
    
    try {
      // Check if there's already a share for this user and knowledge base
      const { data: existingShare, error: checkError } = await supabase
        .from('knowledge_base_shares')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId)
        .eq('user_id', userId)
        .maybeSingle();
      
      if (checkError) throw checkError;
      
      if (existingShare) {
        // Update the existing share
        const { error: updateError } = await supabase
          .from('knowledge_base_shares')
          .update({ 
            access_level: accessLevel,
            shared_by: user.id,
            shared_at: new Date().toISOString()
          })
          .eq('id', existingShare.id);
        
        if (updateError) throw updateError;
      } else {
        // Create a new share
        const { error: insertError } = await supabase
          .from('knowledge_base_shares')
          .insert({
            knowledge_base_id: knowledgeBaseId,
            user_id: userId,
            access_level: accessLevel,
            shared_by: user.id
          });
        
        if (insertError) throw insertError;
      }
      
      toast({
        title: 'Success',
        description: 'Knowledge base shared successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error sharing knowledge base:', error);
      toast({
        title: 'Error',
        description: 'Failed to share knowledge base',
        variant: 'destructive'
      });
      return false;
    }
  };
  
  // Update share access level
  const updateShareAccessLevel = async (
    shareId: string,
    accessLevel: 'View' | 'Edit' | 'Manage'
  ) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_shares')
        .update({ access_level: accessLevel })
        .eq('id', shareId);
      
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Access level updated successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error updating access level:', error);
      toast({
        title: 'Error',
        description: 'Failed to update access level',
        variant: 'destructive'
      });
      return false;
    }
  };
  
  // Remove a share
  const removeShare = async (shareId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_shares')
        .delete()
        .eq('id', shareId);
      
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'Share removed successfully',
      });
      
      return true;
    } catch (error) {
      console.error('Error removing share:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove share',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    ...knowledgeBaseCore,
    addDocument,
    updateDocument,
    deleteDocument,
    getKnowledgeBaseShares,
    shareKnowledgeBase,
    updateShareAccessLevel,
    removeShare,
    sharesLoading
  };
}